import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ApiStatus } from '../../types';
import {
    GenericBladeAEM,
    GenericBladeCancelPayload,
    GenericBladeFetchPayload,
    GenericBladeInfo,
    GenericBladeState,
    GenericBladeStateParams,
    GenericBladeSubmitPayload,
    GenericBladeSubmitRejectedPayload,
    NodePayload,
} from './genericBladeTypes';

const initialState: GenericBladeState = {};

const genericBladeSlice = createSlice({
    name: 'genericBlade',
    initialState,
    reducers: {
        genericBladeFetch(state, action: PayloadAction<string>) {
            const id = action.payload;

            state[id].apiFetchStatus = ApiStatus.Pending;
            state[id].apiFetchErrorMessage = undefined;
        },
        genericBladeFetchFulfilled(
            state,
            action: PayloadAction<GenericBladeFetchPayload>,
        ) {
            const { id, fetchPayload: { content, ...data } } = action.payload;

            if (content) {
                try {
                    state[id].content = JSON.parse(content);
                } catch (e) {
                    console.log('Invalid JSON - Content');
                }
            }
            state[id].api = data;
            state[id].apiFetchStatus = ApiStatus.Fulfilled;
        },
        genericBladeFetchRejected(
            state,
            action: PayloadAction<GenericBladeSubmitRejectedPayload>,
        ) {
            const { id, apiErrorMessage } = action.payload;

            state[id].apiFetchStatus = ApiStatus.Rejected;
            state[id].apiFetchErrorMessage = apiErrorMessage;
        },
        genericBladeSubmit(
            state,
            action: PayloadAction<GenericBladeSubmitPayload>,
        ) {
            const { id } = action.payload;

            state[id].apiSubmitStatus = ApiStatus.Pending;
            state[id].apiSubmitErrorMessage = undefined;
            state[id].apiCancelErrorMessage = undefined;
        },
        genericBladeSubmitFulfilled(
            state,
            action: PayloadAction<GenericBladeSubmitPayload>,
        ) {
            const { id, submittedPayload } = action.payload;

            state[id].submittedPayload = submittedPayload;
            state[id].apiSubmitStatus = ApiStatus.Fulfilled;
        },
        genericBladeSubmitRejected(
            state,
            action: PayloadAction<GenericBladeSubmitRejectedPayload>,
        ) {
            const { id, apiErrorMessage } = action.payload;

            state[id].apiSubmitStatus = ApiStatus.Rejected;
            state[id].apiSubmitErrorMessage = apiErrorMessage;
        },
        genericBladeCancel(
            state,
            action: PayloadAction<GenericBladeCancelPayload>,
        ) {
            const { id } = action.payload;

            state[id].apiSubmitErrorMessage = undefined;
            state[id].apiCancelErrorMessage = undefined;

            if (state[id].urlCancelEndPoint) {
                state[id].apiCancelStatus = ApiStatus.Pending;
            }
        },
        genericBladeCancelFulfilled(
            state,
            action: PayloadAction<GenericBladeCancelPayload>,
        ) {
            const { id, cancelPayload } = action.payload;

            state[id].cancelPayload = cancelPayload;
            state[id].apiCancelStatus = ApiStatus.Fulfilled;
        },
        genericBladeCancelRejected(
            state,
            action: PayloadAction<GenericBladeSubmitRejectedPayload>,
        ) {
            const { id, apiErrorMessage } = action.payload;

            state[id].apiCancelStatus = ApiStatus.Rejected;
            state[id].apiCancelErrorMessage = apiErrorMessage;
        },
        genericBladeUpdateCms(
            state,
            action: PayloadAction<{ id: string, content: GenericBladeAEM}>,
        ) {
            const { id, content } = action.payload;

            state[id].content = content;
        },
        genericBladeUpdateFetchUrl(
            state,
            action: PayloadAction<{ id: string, url: string}>,
        ) {
            const { id, url } = action.payload;

            state[id].urlFetchEndPoint = url;
        },
        genericBladeUpdateSubmitUrl(
            state,
            action: PayloadAction<{ id: string, url: string}>,
        ) {
            const { id, url } = action.payload;

            state[id].urlSubmitEndPoint = url;
        },
        genericBladeUpdateCancelUrl(
            state,
            action: PayloadAction<{ id: string, url: string}>,
        ) {
            const { id, url } = action.payload;

            state[id].urlCancelEndPoint = url;
        },
        genericBladeUpdateContentParams(
            state,
            action: PayloadAction<{id: string, contentParams: GenericBladeStateParams}>,
        ) {
            const { id, contentParams } = action.payload;

            state[id].contentParams = {
                ...state[id].contentParams,
                ...contentParams,
            };
        },
        genericBladeDropDownChange(state, action: PayloadAction<{ id: string, selectedValue: NodePayload}>) {
            const { id, selectedValue } = action.payload;

            if (!state[id]) {
                state[id] = {} as GenericBladeInfo;
            }
            state[id].selectedValue = selectedValue;
        },
        resetGenericBladeStatus(state, action: PayloadAction<string>) {
            const id = action.payload;

            state[id].apiFetchStatus = ApiStatus.Idle;
            state[id].apiSubmitStatus = ApiStatus.Idle;
            state[id].submittedPayload = undefined;
        },
    },
});

export const {
    genericBladeFetch,
    genericBladeFetchFulfilled,
    genericBladeFetchRejected,
    genericBladeSubmit,
    genericBladeSubmitFulfilled,
    genericBladeSubmitRejected,
    genericBladeCancel,
    genericBladeCancelFulfilled,
    genericBladeCancelRejected,
    genericBladeUpdateCms,
    genericBladeUpdateFetchUrl,
    genericBladeUpdateSubmitUrl,
    genericBladeUpdateCancelUrl,
    genericBladeUpdateContentParams,
    genericBladeDropDownChange,
    resetGenericBladeStatus,
} = genericBladeSlice.actions;

export const genericBladeReducer = genericBladeSlice.reducer;
